import { useEffect, useState } from 'react';

const SYMBOLS = ['NIFTY', 'BANKNIFTY', 'FINNIFTY', 'SENSEX'];
const BIASES = ['Bullish', 'Bearish', 'Sideways'];

export default function TradePlanPage() {
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [symbol, setSymbol] = useState('NIFTY');
  const [bias, setBias] = useState('Sideways');
  const [manualEntry, setManualEntry] = useState('');
  const [levelsNote, setLevelsNote] = useState('');
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState({ type: '', text: '' });
  const [plans, setPlans] = useState([]);
  const [listLoading, setListLoading] = useState(true);

  const apiBase = '/api/api_trade_plan.php';

  async function loadPlan(d, sym) {
    setLoading(true);
    setMsg({ type: '', text: '' });
    try {
      const res = await fetch(`${apiBase}?action=get&date=${d}&symbol=${sym}`, { credentials: 'include' });
      const j = await res.json();
      if (j.plan) {
        setBias(j.plan.bias || 'Sideways');
        setManualEntry(j.plan.manual_entry || '');
        setLevelsNote(j.plan.levels_note || '');
      } else {
        setBias('Sideways');
        setManualEntry('');
        setLevelsNote('');
      }
    } catch (e) {
      setMsg({ type: 'err', text: 'Failed to load plan' });
    } finally {
      setLoading(false);
    }
  }

  async function savePlan() {
    setLoading(true);
    setMsg({ type: '', text: '' });
    try {
      const res = await fetch(apiBase, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          action: 'save',
          plan_date: date,
          symbol,
          bias,
          manual_entry: manualEntry,
          levels_note: levelsNote,
        }),
      });
      const j = await res.json();
      if (!res.ok) throw new Error(j.error || 'save failed');
      setMsg({ type: 'ok', text: 'Saved successfully' });
      loadList();
    } catch (e) {
      setMsg({ type: 'err', text: e.message });
    } finally {
      setLoading(false);
    }
  }

  async function deletePlan(id) {
    if (!confirm('Delete this plan?')) return;
    try {
      const res = await fetch(`${apiBase}?action=delete&id=${id}`, { credentials: 'include' });
      const j = await res.json();
      if (!res.ok) throw new Error(j.error);
      setMsg({ type: 'ok', text: 'Deleted' });
      loadList();
      if (plans.find(p => p.id === id)) loadPlan(date, symbol);
    } catch (e) {
      setMsg({ type: 'err', text: e.message });
    }
  }

  async function loadList() {
    setListLoading(true);
    try {
      const res = await fetch(`${apiBase}?action=list&limit=20`, { credentials: 'include' });
      const j = await res.json();
      setPlans(j.plans || []);
    } catch {
      setPlans([]);
    } finally {
      setListLoading(false);
    }
  }

  useEffect(() => {
    loadPlan(date, symbol);
  }, [date, symbol]);

  useEffect(() => {
    loadList();
  }, []);

  const onSelectPlan = (p) => {
    setDate(p.plan_date);
    setSymbol(p.symbol);
    // loadPlan will trigger via effect
  };

  return (
    <div className="tp-wrap">
      <div className="tp-head">
        <h2><i className="bi bi-journal-text" /> Date-wise Trade Plan</h2>
        <span className="tp-meta">All data via PHP API — React UI</span>
      </div>

      <div className="tp-card">
        <div className="tp-row">
          <label>
            Date
            <input type="date" value={date} onChange={e => setDate(e.target.value)} />
          </label>
          <label>
            Symbol
            <select value={symbol} onChange={e => setSymbol(e.target.value)}>
              {SYMBOLS.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
          <div style={{ flex: 1 }} />
          <button className="tp-btn" onClick={savePlan} disabled={loading}>
            {loading ? 'Saving…' : 'Save Plan'}
          </button>
        </div>

        <div className="tp-row" style={{ marginBottom: 8 }}>
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-400 mb-1">Bias</div>
            <div className="tp-bias">
              {BIASES.map(b => (
                <button
                  key={b}
                  type="button"
                  className={bias === b ? (b === 'Bullish' ? 'on-bull' : b === 'Bearish' ? 'on-bear' : 'on-side') : ''}
                  onClick={() => setBias(b)}
                >
                  {b}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div style={{ marginBottom: 12 }}>
          <label className="text-xs uppercase tracking-widest text-slate-400">Manual Entry / Notes</label>
          <textarea
            className="tp-area"
            value={manualEntry}
            onChange={e => setManualEntry(e.target.value)}
            placeholder="Key observations, levels, strategy..."
          />
        </div>

        <div>
          <label className="text-xs uppercase tracking-widest text-slate-400">Key Levels / Notes</label>
          <input
            type="text"
            className="tp-area"
            style={{ minHeight: 60, height: 60 }}
            value={levelsNote}
            onChange={e => setLevelsNote(e.target.value)}
            placeholder="Support / Resistance, targets..."
          />
        </div>

        <div style={{ marginTop: 10, display: 'flex', gap: 8, alignItems: 'center' }}>
          <button className="tp-btn" onClick={savePlan} disabled={loading}>Save / Update</button>
          <span className={`tp-msg ${msg.type === 'ok' ? 'ok' : 'err'}`}>{msg.text}</span>
        </div>
      </div>

      <div className="tp-card">
        <div className="flex items-center justify-between mb-2">
          <div className="font-semibold">Recent Plans</div>
          <button className="tp-btn" style={{ background: 'transparent', border: '1px solid var(--border-color)', color: 'inherit' }} onClick={loadList}>Refresh</button>
        </div>
        {listLoading ? <div className="text-sm text-slate-400">Loading…</div> : (
          <table className="tp-list">
            <thead>
              <tr>
                <th>Date</th>
                <th>Symbol</th>
                <th>Bias</th>
                <th>Updated</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {plans.length === 0 && <tr><td colSpan={5} style={{ textAlign: 'center', color: 'var(--text-dim)' }}>No plans yet.</td></tr>}
              {plans.map(p => (
                <tr key={p.id} className="tp-row-plan" onClick={() => onSelectPlan(p)}>
                  <td>{p.plan_date}</td>
                  <td><strong>{p.symbol}</strong></td>
                  <td><span className={`tp-tag ${p.bias}`}>{p.bias}</span></td>
                  <td className="text-xs text-slate-400">{p.updated_at ? p.updated_at.slice(0, 16).replace('T', ' ') : ''}</td>
                  <td>
                    <button className="tp-del" onClick={(e) => { e.stopPropagation(); deletePlan(p.id); }}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
